import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { emptyArray } from 'ui-components/default-props/empty';
import { Button } from 'ui-components/components/button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import { PackageDropdown } from '../../../fulfillment-planner/package/dropdown';
import { useGetPackageIds } from '../../../fulfillment-planner/package/dropdown/package-id-helpers';
import { ShippingServiceDropdownSelectedValue } from '../../shipping-service-selector/shipping-service-dropdown-selected-value';
import { ShippingServiceSearchableDropdown } from '../../shipping-service-selector/shipping-service-searchable-dropdown';
import Global from '../../../store/global-selectors';

import { useServiceMappingDropdowns } from './use-service-mapping-dropdowns';
import { messages } from './messages';
import style from './map-requested-service-modal.module.css';
import { FillMethods } from '../../../enums/fill-methods';

const renderSelectedValue = ({ serviceId, sellerLabelProviderId, serviceCollectionGuid, isTemplate }) => {
  if (!serviceCollectionGuid && !serviceId) {
    return;
  }

  return (
    <ShippingServiceDropdownSelectedValue
      isTemplate={isTemplate}
      sellerLabelProviderId={sellerLabelProviderId}
      serviceCollectionGuid={serviceCollectionGuid}
      serviceId={serviceId}
    />
  );
};

/**
 * A modal that lets the merchant map a requested service code (e.g. "Economy" or "Standard") to a
 * specific shipping service or rate shopper, and package.
 * @param {object} props
 * @param {string} props.storeGuid - The GUID of the store for which the service mapping is being created/edited
 * @param {boolean} [props.isLoading]
 * @param {string} [props.requestedServiceCode] - The requested service code to map (e.g. "Economy" or "Standard")
 * @param {(requestedServiceMapping, sellerLabelProviderId) => void} [props.saveRequestedServiceMapping] - Method called to save the service mapping
 * @param {string} [props.selectedPackageTypeId]
 * @param {string} [props.selectedServiceId]
 * @param {string} [props.selectedServiceCollectionGuid]
 * @returns {React.ReactElement}
 */
export const MapRequestedServiceModal = ({
  isLoading = false,
  saveRequestedServiceMapping,
  requestedServiceCode = '',
  selectedPackageTypeId = '',
  selectedServiceCollectionGuid = '',
  selectedServiceId = '',
  storeGuid,
}) => {
  const packages = useSelector(Global.selectSellerPackages) || emptyArray;
  const [currentService, setCurrentService] = useState({
    serviceId: selectedServiceId,
    sellerLabelProviderId: undefined,
    serviceCollectionGuid: selectedServiceCollectionGuid,
  });
  const [packageTypeId, setPackageTypeId] = useState(selectedPackageTypeId);
  const selectedPackage = useMemo(() => packages.find((pkg) => pkg.packageTypeId === packageTypeId), [packages, packageTypeId]);
  const selectedPackageIds = useGetPackageIds([selectedPackage], currentService.serviceId);
  const storeName = useSelector((state) => {
    const stores = Global.selectSellerStores(state);
    const matchingStore = stores.find((store) => store.storeGuid === storeGuid);
    return matchingStore ? matchingStore.name : '';
  });
  const { serviceGroups, packageGroups, selectedServiceOption } = useServiceMappingDropdowns({
    serviceId: currentService.serviceId,
    serviceCollectionGuid: currentService.serviceCollectionGuid,
    sellerLabelProviderId: currentService.sellerLabelProviderId,
  });

  const onPackageSelected = (newPackageId) => {
    const splitPackageId = newPackageId.split('-');
    const newPackageTypeId = splitPackageId[0];

    if (newPackageTypeId) {
      setPackageTypeId(newPackageTypeId);
    }
  };

  const onServiceSelected = useCallback(({ serviceId, sellerLabelProviderId, serviceCollectionGuid }) => {
    setCurrentService({ serviceId, sellerLabelProviderId, serviceCollectionGuid });
  }, []);

  const onSave = useCallback(() => {
    saveRequestedServiceMapping(
      {
        fillMethod: FillMethods.shipping,
        requestedServiceCode,
        serviceId: currentService.serviceId,
        serviceCollectionGuid: currentService.serviceCollectionGuid,
        packageTypeId,
        storeGuid,
      },
      currentService.sellerLabelProviderId,
    );
  }, [
    saveRequestedServiceMapping,
    requestedServiceCode,
    currentService.serviceId,
    currentService.serviceCollectionGuid,
    currentService.sellerLabelProviderId,
    packageTypeId,
    storeGuid,
  ]);

  return (
    <Modal className={style['map-requested-service-modal']}>
      <ModalHeader>
        <FormattedMessage {...messages.mapRequestedServiceTitle} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        {isLoading && <LoadingIndicator className={style['loading-indicator']} />}

        {!isLoading && (
          <FlexColumn gap={Gap.Space16}>
            <Text
              textType={TextType.Paragraph}
              value={
                <FormattedMessage
                  {...messages.mapRequestedServiceServiceLabel}
                  values={{
                    requestedServiceCode: <b>&quot;{requestedServiceCode}&quot;</b>,
                  }}
                />
              }
            />

            <ShippingServiceSearchableDropdown
              onSelected={onServiceSelected}
              renderSelectedValue={renderSelectedValue}
              selectedOption={selectedServiceOption}
              serviceGroups={serviceGroups}
            />

            <PackageDropdown
              className={style.dropdown}
              onChange={onPackageSelected}
              packageGroups={packageGroups}
              selectedPackageIds={selectedPackageIds}
              serviceCollectionGuid={currentService.serviceCollectionGuid}
            />

            <Text
              className={style.instructions}
              textType={TextType.Caption}
              value={<FormattedMessage {...messages.mapRequestedServiceInstructionOne} />}
            />

            <Text
              className={style.instructions}
              textType={TextType.Caption}
              value={<FormattedMessage {...messages.mapRequestedServiceInstructionTwo} values={{ storeName: <b>{storeName}</b> }} />}
            />
          </FlexColumn>
        )}
      </ModalBody>
      {!isLoading && (
        <ModalFooter className={style['modal-footer']}>
          <ModalFooterCancelButton />
          <Button onClick={onSave} theme='primary'>
            <FormattedMessage {...messages.mapRequestedServiceSaveMapping} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};
